import { prop } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { noop } from 'js/utils/methods.util';
import { ManagedPopup } from 'js/components/common/Popup';
import { useCombinedOnClose } from 'js/components/common/PopupManager';
import { Loader } from 'js/components/common/Loader';
import { useTranslations } from 'js/hooks/i18n';
import { useGetErrorMessage } from 'js/hooks/server';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { useValidateLoginSession } from 'server';
import { useUserSelector } from 'js/store/hooks';
import * as pas from 'js/hooks/auth/pas';

import { CAFMultiFactorAuthentication, UnicoMultiFactorAuthentication } from './components';

import style from './MultiFactorAuthenticationPopup.scss';

const popupClassNames = {
    inner: style.popupInner
};

const layerClassNames = {
    layout: style.layout
};

export const MultiFactorAuthenticationPopup = ({
    id,
    title,
    controls,
    onSuccess,
    onCancel,
    onCustomValidate,
    provider: providedProvider,
    properties: providedProperties = []
}) => {
    const translate = useTranslations('playtech.mobile.system.MFA.popup.');
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const getErrorMessage = useGetErrorMessage({ translate });
    const showNotification = useShowNotification();
    const combinedOnCancel = useCombinedOnClose(onCancel, id);
    const combinedOnSuccess = useCombinedOnClose(onSuccess, id);
    const [error, setError] = useState();
    const [response, setResponse] = useState(null);
    const [provider, setProvider] = useState(providedProvider);
    const [properties, setProperties] = useState(providedProperties || []);
    const validateLoginSession = useValidateLoginSession();
    const providerProperties = useMemo(() => Object.fromEntries(properties.map(({
        key,
        value
    }) => [key, value])), [properties]);

    useEffect(() => {
        if (provider) {
            return;
        }

        (async () => {
            const {
                sessionValidationByMfa: {
                    provider: receivedProvider,
                    properties: receivedProperties
                } = {}
            } = await validateLoginSession();

            receivedProvider && setProvider(receivedProvider);
            receivedProperties && setProperties(receivedProperties);
        })();
    }, [
        provider,
        properties,
        validateLoginSession
    ]);

    useEffect(() => {
        (async () => {
            if (!error && !response) {
                return;
            }

            try {
                if (onCustomValidate) {
                    await onCustomValidate(JSON.stringify(error || response));
                } else if (isLoggedIn) {
                    await validateLoginSession({
                        sessionValidationByMfaToken: {
                            result: JSON.stringify(error || response)
                        }
                    });
                } else {
                    await pas.validateMfaToken(JSON.stringify(error || response));
                }

                if (response) {
                    combinedOnSuccess();
                } else {
                    combinedOnCancel();
                }
            } catch (exception) {
                showNotification(getErrorMessage(exception), { variant: 'error' });

                combinedOnCancel();
            }
        })();
    }, [
        error,
        isLoggedIn,
        response,
        onCustomValidate,
        combinedOnCancel,
        combinedOnSuccess,
        showNotification,
        validateLoginSession,
        getErrorMessage
    ]);

    return (
        <ManagedPopup
            id={id}
            isModal={true}
            onOverlayClick={noop}
            controls={controls}
            className={style.container}
            classNames={popupClassNames}
            layerClassNames={layerClassNames}
        >
            {
                provider === 'caf' && (
                    <CAFMultiFactorAuthentication
                        title={title}
                        setError={setError}
                        setResponse={setResponse}
                        provider={provider}
                        {...providerProperties}
                    />
                )
            }
            {
                provider === 'unico' && (
                    <UnicoMultiFactorAuthentication
                        title={title}
                        setError={setError}
                        setResponse={setResponse}
                        provider={provider}
                        {...providerProperties}
                    />
                )
            }
            {
                !provider && (
                    <Loader />
                )
            }
        </ManagedPopup>
    );
};
